import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios, { AxiosError } from "axios";

export const fetchCartData = createAsyncThunk(
  "getCartDetails",
  async (formData: FormData, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${import.meta.env.VITE_API_URL}/getCartDetails`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${import.meta.env.VITE_API_KEY}`,
          },
        },
      );

      return response.data;
    } catch (error) {
      throw rejectWithValue((error as AxiosError)?.response?.data);
    }
  },
);

export const updateDraftOrder = createAsyncThunk(
  "updatedraftorder",
  async (formData: FormData, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${import.meta.env.VITE_API_URL}/updatedraftorder`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${import.meta.env.VITE_API_KEY}`,
          },
        },
      );

      return response.data;
    } catch (error) {
      throw rejectWithValue((error as AxiosError)?.response?.data);
    }
  },
);

export const applyCustomerDiscount = createAsyncThunk(
  "applyCustomerDiscount",
  async (formData: FormData, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${import.meta.env.VITE_API_URL}/Applycustomerciscount`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${import.meta.env.VITE_API_KEY}`,
          },
        },
      );

      return response.data;
    } catch (error) {
      throw rejectWithValue((error as AxiosError)?.response?.data);
    }
  },
);

export const updateCartQty = createAsyncThunk(
  "updateCartQty",
  async (formData: FormData, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${import.meta.env.VITE_API_URL}/updatecartqyt`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${import.meta.env.VITE_API_KEY}`,
          },
        },
      );

      return response.data;
    } catch (error) {
      throw rejectWithValue((error as AxiosError)?.response?.data);
    }
  },
);

export const removeCartItem = createAsyncThunk(
  "remove_cart_item",
  async (formData: FormData, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${import.meta.env.VITE_API_URL}/remove_cart_item`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${import.meta.env.VITE_API_KEY}`,
          },
        },
      );

      return response.data;
    } catch (error) {
      throw rejectWithValue((error as AxiosError)?.response?.data);
    }
  },
);

export const removeCartDetails = createAsyncThunk(
  "removecartdetails",
  async (formData: FormData, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${import.meta.env.VITE_API_URL}/removecartdetails`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${import.meta.env.VITE_API_KEY}`,
          },
        },
      );

      return response.data;
    } catch (error) {
      throw rejectWithValue((error as AxiosError)?.response?.data);
    }
  },
);

interface CartState {
  loading: boolean;
  data: any;
  error: string;
}

const initialState: CartState = {
  loading: false,
  data: null,
  error: "",
};

export const cartSlice = createSlice({
  name: "cart",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchCartData.pending, (state) => {
        state.loading = true;
        state.error = "";
      })
      .addCase(fetchCartData.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload;
        state.error = "";
      })
      .addCase(fetchCartData.rejected, (state, action) => {
        state.loading = false;
        state.data = null;
        state.error = action.error.message || "Failed to fetch cart data";
      });
    builder
      .addCase(updateDraftOrder.pending, (state) => {
        state.loading = true;
        state.error = "";
      })
      .addCase(updateDraftOrder.fulfilled, (state) => {
        state.loading = false;
        state.error = "";
      })
      .addCase(updateDraftOrder.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to update draft order";
      });
    builder
      .addCase(applyCustomerDiscount.pending, (state) => {
        state.loading = true;
        state.error = "";
      })
      .addCase(applyCustomerDiscount.fulfilled, (state) => {
        state.loading = false;
        state.error = "";
      })
      .addCase(applyCustomerDiscount.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to apply discount";
      });
    builder
      .addCase(updateCartQty.pending, (state) => {
        state.loading = true;
        state.error = "";
      })
      .addCase(updateCartQty.fulfilled, (state) => {
        state.loading = false;
        state.error = "";
      })
      .addCase(updateCartQty.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to update cart quantity";
      });
    builder
      .addCase(removeCartItem.pending, (state) => {
        state.loading = true;
        state.error = "";
      })
      .addCase(removeCartItem.fulfilled, (state) => {
        state.loading = false;
        state.error = "";
      })
      .addCase(removeCartItem.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to remove cart item";
      });
    builder
      .addCase(removeCartDetails.pending, (state) => {
        state.loading = true;
        state.error = "";
      })
      .addCase(removeCartDetails.fulfilled, (state) => {
        state.loading = false;
        state.error = "";
      })
      .addCase(removeCartDetails.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to remove cart details";
      });
  },
});

export default cartSlice.reducer;
